// base
import { useMemo } from 'react';

// models
import type { ResultError, ResultBaseProps } from 'modules';

// libraries
import {
  type InfiniteData,
  type QueryKey,
  useInfiniteQuery,
  type UseInfiniteQueryOptions,
  type UseInfiniteQueryResult
} from '@tanstack/react-query';

/**
 * `useCommonInfiniteQuery` 훅의 매개변수를 정의하는 인터페이스입니다.
 *
 * @template TReqData - 요청 데이터의 타입
 * @template TResData - 응답 데이터의 타입, `ResultBaseProps`을 확장해야 합니다.
 * @template TError - 에러의 타입, 기본값은 `ResultError`입니다.
 */
export interface UseCommonInfiniteQueryParams<TReqData, TResData extends ResultBaseProps, TError> {
  /**
   * 요청에 사용할 데이터입니다.
   * `size`는 선택적으로 포함될 수 있습니다.
   */
  data: TReqData & { size?: number };

  /**
   * `useInfiniteQuery`에 전달할 추가 옵션입니다.
   * `queryKey`와 `queryFn`은 이 훅에서 처리하므로 제외됩니다.
   */
  options?: Omit<
    UseInfiniteQueryOptions<TResData, TError, InfiniteData<TResData>, TResData, QueryKey, number>,
    'queryKey' | 'queryFn'
  >;

  /**
   * 쿼리 키를 생성하는 함수입니다.
   *
   * @param data - 요청 데이터
   * @returns 쿼리 키 배열
   */
  queryKeyFactory: (data: TReqData) => QueryKey;

  /**
   * 데이터를 가져오는 쿼리 함수입니다.
   *
   * @param data - 요청 데이터
   * @param page - 페이지 정보 (`page`와 `size`)
   * @returns 응답 데이터를 반환하는 프로미스
   */
  queryFn: (
    data: TReqData,
    page: {
      page: number;
      size: number;
    }
  ) => Promise<TResData>;
}

/**
 * `useCommonInfiniteQuery` 훅의 반환 타입입니다.
 *
 * @template TData - 데이터의 타입
 * @template TError - 에러의 타입
 */
export type UseCommonInfiniteQueryResult<TData, TError> = UseInfiniteQueryResult<TData, TError>;

/**
 * 무한 스크롤을 지원하는 공통 쿼리 훅입니다.
 *
 * @template TReqData - 요청 데이터의 타입
 * @template TResData - 응답 데이터의 타입, `ResultBaseProps`을 확장해야 합니다.
 * @template TError - 에러의 타입, 기본값은 `ResultError`입니다.
 *
 * @param props - 훅에 필요한 매개변수들
 * @returns `useInfiniteQuery`의 결과를 반환합니다.
 *
 * @example
 * const {
 *   data,
 *   fetchNextPage,
 *   hasNextPage,
 *   isFetchingNextPage
 * } = useCommonInfiniteQuery({
 *   data: requestData,
 *   queryKeyFactory: (data) => ['myQueryKey', data],
 *   queryFn: fetchFunction,
 * });
 */
export const useCommonInfiniteQuery = <
  TReqData,
  TResData extends ResultBaseProps,
  TError = ResultError
>(
  props: UseCommonInfiniteQueryParams<TReqData, TResData, TError>
): UseCommonInfiniteQueryResult<InfiniteData<TResData>, TError> => {
  const { data, options, queryKeyFactory, queryFn } = props;

  // 쿼리 키를 생성합니다.
  const queryKey = useMemo(() => {
    const result = queryKeyFactory(data);
    return [...result];
  }, [data, queryKeyFactory]);

  // 무한 쿼리를 실행합니다.
  const queryResult = useInfiniteQuery<TResData, TError, InfiniteData<TResData>, QueryKey, number>({
    queryKey,
    queryFn: ({ pageParam = 0 }) => queryFn(data, { page: pageParam, size: data.size || 10 }),
    /**
     * 다음 페이지의 페이지 번호를 결정하는 함수입니다.
     *
     * @param lastPage - 마지막으로 가져온 페이지의 데이터
     * @param allPages - 지금까지 가져온 모든 페이지의 데이터 배열
     * @returns 다음 페이지의 번호 또는 `undefined` (더 이상 페이지가 없을 때)
     */
    getNextPageParam: (lastPage, allPages) => {
      const currentPage = allPages.length;
      const totalPageCount = lastPage.totalMap.totalPages || 0;

      if (currentPage < totalPageCount) {
        return currentPage;
      } else {
        return undefined;
      }
    },
    /**
     * 이전 페이지의 페이지 번호를 결정하는 함수입니다.
     *
     * @param lastPage - 마지막으로 가져온 페이지의 데이터
     * @param allPages - 지금까지 가져온 모든 페이지의 데이터 배열
     * @returns 이전 페이지의 번호 또는 `undefined` (더 이상 페이지가 없을 때)
     */
    getPreviousPageParam: (_, allPages) => {
      const currentPage = allPages.length;

      if (currentPage > 1) {
        return currentPage - 2;
      } else {
        return undefined;
      }
    },
    // 초기 페이지 파라미터 설정
    initialPageParam: 0,
    // 추가 옵션 적용
    ...options
  });

  // 쿼리 결과 반환
  return queryResult;
};
